<template>
  <div class="flex flex-col justify-between flex-grow h-full min-w-0 m-0">
    <template v-if="currentChatCanReply !== undefined">
      <banner
        v-if="!currentChat.can_reply && isAWhatsAppChannel"
        :color-scheme="'bg-[#FFFBEB] border border-[#FEF3C7] !text-[#78350F] !text-sm !px-3 !py-2 !h-10'"
        :banner-message="
          $t('CONVERSATION.THE_SESSION_TO_REPLY_ENDED', {
            sessionTime: $t('CONVERSATION.24_HOUR'),
          })
        "
      />
      <banner
        v-else-if="!currentChat.can_reply && isAFacebookInbox"
        :color-scheme="'bg-[#FFFBEB] border border-[#FEF3C7] !text-[#78350F] !text-sm !px-3 !py-2 !h-10'"
        :banner-message="$t('CONVERSATION.CANNOT_REPLY_7_DAYS')"
      />
      <banner
        v-else-if="!currentChat.can_reply && isAFacebook2Inbox"
        :color-scheme="'bg-[#FFFBEB] border border-[#FEF3C7] !text-[#78350F] !text-sm !px-3 !py-2 !h-10'"
        :banner-message="$t('CONVERSATION.CANNOT_REPLY_READ_ONLY')"
      />
      <banner
        v-else-if="!currentChat.can_reply"
        :color-scheme="'bg-[#FFFBEB] border border-[#FEF3C7] !text-[#78350F] !text-sm !px-3 !py-2 !h-10'"
        :banner-message="$t('CONVERSATION.CANNOT_REPLY')"
        :href-link="facebookReplyPolicy"
        :href-link-text="$t('CONVERSATION.24_HOURS_WINDOW')"
      />
    </template>

    <div class="flex justify-end">
      <woot-button
        variant="smooth"
        size="tiny"
        color-scheme="secondary"
        class="rounded-bl-calc rtl:rotate-180 rounded-tl-calc fixed top-[9.5rem] md:top-[6.25rem] z-10 bg-white dark:bg-slate-700 border-slate-50 dark:border-slate-600 border-solid border border-r-0 box-border"
        :icon="isRightOrLeftIcon"
        @click="onToggleContactPanel"
      />
    </div>
    <!-- MESSAGES -->
    <div
      v-if="hasMessage"
      class="conversation-panel flex-shrink flex-grow basis-px flex flex-col overflow-y-auto custom-scroll bg-neutral-100"
    >
      <ul
        class="read-message-container flex-shrink flex-grow basis-px flex gap-2 flex-col relative m-0 px-5 pt-3"
        :class="{
          'pb-0': getUnReadMessages.length,
          'pb-3': !getUnReadMessages.length,
        }"
      >
        <transition name="slide-up">
          <li v-if="shouldShowSpinner" class="min-h-[4rem]">
            <span class="spinner message" />
          </li>
        </transition>
        <div
          v-if="!hasInboxPermission('Inbox see conversations history')"
          class="flex justify-center items-center"
        >
          <system-message
            :message-content="$t('DO_NOT_HAVE_PERMIT_TO_VIEW_HISTORY')"
          />
        </div>
        <message
          v-for="message in getReadMessages"
          :key="message.id"
          class="message--read ph-no-capture"
          data-clarity-mask="True"
          :data="message"
          :is-a-tweet="isATweet"
          :is-a-tiktok="isATiktokChannel"
          :class="{ wa: isSomeOfOnlineWaInbox }"
          :deleteable="!isSomeOfOnlineWaInbox || Boolean(message.delay_hours)"
          :assignable="isAssignable"
          :is-a-whats-app-channel="isAWhatsAppChannel"
          :has-instagram-story="hasInstagramStory"
          :is-web-widget-inbox="isAWebWidgetInbox"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :in-reply-to="getInReplyToMessage(message)"
          :d99d-features="d99dFeatures"
        />
        <li
          v-show="unreadMessageCount != 0 && !manualUnread"
          class="unread--toast"
        >
          <div class="px-3 bg-neutral-100 z-10 my-2.5 mx-auto">
            <span>
              {{ unreadMessageCount > 9 ? '9+' : unreadMessageCount }}
              {{
                unreadMessageCount > 1
                  ? $t('CONVERSATION.UNREAD_MESSAGES')
                  : $t('CONVERSATION.UNREAD_MESSAGE')
              }}
            </span>
          </div>
        </li>
        <conversation-label-suggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
      </ul>
      <ul
        v-if="hasMessage && getUnReadMessages.length"
        class="unread-message-container flex flex-col gap-2 list-none m-0 w-full px-5 pt-2 pb-3"
        :class="{
          'bg-neutral-200': unreadMessageCount != 0 && !manualUnread,
        }"
      >
        <message
          v-for="message in getUnReadMessages"
          :key="message.id + '-u'"
          class="message--unread ph-no-capture"
          data-clarity-mask="True"
          :data="message"
          :is-a-tweet="isATweet"
          :is-a-tiktok="isATiktokChannel"
          :class="{
            wa: isSomeOfOnlineWaInbox,
          }"
          :deleteable="!isSomeOfOnlineWaInbox || Boolean(message.delay_hours)"
          :assignable="isAssignable"
          :is-a-whats-app-channel="isAWhatsAppChannel"
          :has-instagram-story="hasInstagramStory"
          :is-web-widget-inbox="isAWebWidgetInbox"
          :d99d-features="d99dFeatures"
          :inbox-supports-reply-to="inboxSupportsReplyTo"
          :in-reply-to="getInReplyToMessage(message)"
        />
      </ul>
    </div>
    <message-empty v-else />
    <!-- REPLY BOX -->
    <div v-if="isAnyoneTyping" class="flex items-center gap-2 px-5 pb-3">
      <div class="flex flex-row items-end gap-1">
        <span class="text-gray-400 text-sm font-semibold">
          {{ typingUserNames }}
        </span>
        <div class="typing-indicator transparent">
          <div class="dot" />
          <div class="dot" />
          <div class="dot" />
        </div>
      </div>
    </div>
    <div class="conversation-footer">
      <reply-box-wrapper />
    </div>
    <online99-fb2-private-reply
      v-if="!!online99Fb2PrivateReplyMessage"
      :message="online99Fb2PrivateReplyMessage"
      :on-close="onCancelOnline99Fb2PrivateReply"
    />
  </div>
</template>

<script>
// components
import Message from './Message.vue';
import ConversationLabelSuggestion from 'dashboard/components/widgets/conversation/conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import SystemMessage from '../SystemMessage.vue';
import ReplyBoxWrapper from './ReplyBoxWrapper.vue';
import MessageEmpty from '../MessageEmpty.vue';

// stores and apis
import { computed } from 'vue';
import { mapGetters } from 'vuex';

// mixins
import conversationMixin, {
  filterDuplicateSourceMessages,
} from 'dashboard/mixins/conversations';
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';
import configMixin from 'shared/mixins/configMixin';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import aiMixin from 'dashboard/mixins/aiMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';

// utils
import { getTypingUsersText } from 'dashboard/helper/commons';
import { calculateScrollTop } from 'dashboard/components/widgets/conversation/helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import Online99Fb2PrivateReply from 'dashboard/routes/dashboard/conversation/Online99Fb2PrivateReply.vue';

export default {
  components: {
    Online99Fb2PrivateReply,
    Message,
    ReplyBoxWrapper,
    Banner,
    ConversationLabelSuggestion,
    SystemMessage,
    MessageEmpty,
  },
  mixins: [
    conversationMixin,
    inboxMixin,
    eventListenerMixins,
    configMixin,
    aiMixin,
    hasPermissionMixin,
  ],
  provide() {
    return {
      inboxSupportsReplyTo: computed(() => this.inboxSupportsReplyTo),
    };
  },
  props: {
    isContactPanelOpen: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isLoadingPrevious: false,
      heightBeforeLoad: null,
      conversationPanel: null,
      hasUserScrolled: false,
      isProgrammaticScroll: false,
      online99Fb2PrivateReplyMessage: undefined, // d99d
      messageSentSinceOpened: false,
      labelSuggestions: [],
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentChat: 'getSelectedChat',
      allConversations: 'getAllConversations',
      inboxesList: 'inboxes/getInboxes',
      listLoadingStatus: 'getAllMessagesLoaded',
      getUnreadCount: 'getUnreadCount',
      manualUnread: 'isManualUnread',
      loadingChatList: 'getChatListLoadingStatus',
      appIntegrations: 'integrations/getAppIntegrations',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentAccountId: 'getCurrentAccountId',
    }),
    isSnoozed() {
      return this.currentChat.status === wootConstants.STATUS_TYPE.SNOOZED;
    },
    historyWarningData() {
      return {
        content: 'You have no permission to view chat history',
        content_type: 'text',
        message_type: 2,
        status: 'sent',
        private: false,
        created_at: this.currentChat?.last_activity_at,
      };
    },
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.isEnterprise &&
        this.isAIIntegrationEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isAnyoneTyping() {
      const userList = this.typingUsersList;
      return userList.length !== 0;
    },
    typingUserNames() {
      const userList = this.typingUsersList;

      if (this.isAnyoneTyping) {
        const userListAsName = getTypingUsersText(userList);
        return userListAsName;
      }

      return '';
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return filterDuplicateSourceMessages(messages);
      }
      return messages;
    },
    hasMessage() {
      return this.getMessages.length;
    },
    getReadMessages() {
      return this.readMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    getUnReadMessages() {
      return this.unReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      return (
        (this.currentChat && this.currentChat.dataFetched === undefined) ||
        (!this.listLoadingStatus && this.isLoadingPrevious)
      );
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },

    isATweet() {
      return this.conversationType === 'tweet';
    },

    hasInstagramStory() {
      return this.conversationType === 'instagram_direct_message';
    },
    isRightOrLeftIcon() {
      if (this.isContactPanelOpen) {
        return 'arrow-chevron-right';
      }
      return 'arrow-chevron-left';
    },
    getLastSeenAt() {
      const { contact_last_seen_at: contactLastSeenAt } = this.currentChat;
      return contactLastSeenAt;
    },
    // d99d
    isAssignable() {
      return online99Constants.platform_SendFileFeature;
    },
    d99dFeatures() {
      return {
        fb2Featureable:
          this.isAFacebook2Inbox &&
          this.currentChat.additional_attributes.type !== 'instagram_comment',
        handlePrivateReply: message => {
          this.online99Fb2PrivateReplyMessage = message;
        },
      };
    },
    // .
    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
          } = additionalAttributes;
          return agentReplyTimeWindowMessage;
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAWhatsAppChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      return this.currentChat.unread_count || 0;
    },
    inboxSupportsReplyTo() {
      return (
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO) &&
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.MESSAGE_REPLY_TO
        ) &&
        this.currentChat.can_reply // d99d
      );
    },
    currentChatCanReply() {
      return this.currentChat.can_reply;
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    contactId() {
      return this.currentChat.meta?.sender?.id;
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      if (newChat.id === oldChat.id) {
        return;
      }
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
    },
    currentChatCanReply: {
      handler(newVal) {
        if (newVal === undefined) {
          this.fetchSingleConversation();
        }
      },
      immediate: true,
    },
    hasMessage(newVal) {
      if (newVal && !this.conversationPanel) {
        this.$nextTick(() => this.scrollToBottom());
      }
    },
  },

  created() {
    bus.$on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a new message comes in, we refetch the label suggestions
    bus.$on(BUS_EVENTS.FETCH_LABEL_SUGGESTIONS, this.fetchSuggestions);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    bus.$on(BUS_EVENTS.MESSAGE_SENT, () => {
      this.messageSentSinceOpened = true;
    });
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
  },

  beforeDestroy() {
    this.removeBusListeners();
    this.removeScrollListener();
  },

  methods: {
    async fetchSuggestions() {
      return; // d99d disable it at all
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      if (!this.isEnterprise) {
        return;
      }

      // method available in mixin, need to ensure that integrations are present
      await this.fetchIntegrationsIfRequired();

      if (!this.isAIIntegrationEnabled) {
        return;
      }

      this.labelSuggestions = await this.fetchLabelSuggestions({
        conversationId: this.currentChat.id,
      });

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      if (this.currentChat.id)
        this.$store.dispatch('fetchAllAttachments', this.currentChat.id); // d99d added condition, because on the mount the chat can be empty
    },
    removeBusListeners() {
      bus.$off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    },
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = document.getElementById('message' + messageId);
        if (messageElement) {
          this.isProgrammaticScroll = true;
          messageElement.scrollIntoView({ behavior: 'smooth' });
          messageElement.__vue__?.setupHighlightTimer?.(messageId); // d99d (for url link and reply link)
          this.fetchPreviousMessages(this.conversationPanel.scrollTop); // d99d - added parameter (for reply, without load previous)
        } else {
          this.scrollToBottom();
        }
      });
      this.makeMessagesRead();
    },
    addScrollListener() {
      this.conversationPanel = this.$el.querySelector('.conversation-panel');
      if (!this.conversationPanel) return;
      this.setScrollParams();
      this.conversationPanel.addEventListener('scroll', this.handleScroll);
      this.$nextTick(() => this.scrollToBottom());
    },
    removeScrollListener() {
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
    },
    scrollToBottom() {
      this.selectedTweetId = undefined; // d99d
      this.isProgrammaticScroll = true;
      let relevantMessages = [];

      // label suggestions are not part of the messages list
      // so we need to handle them separately
      let labelSuggestions =
        this.conversationPanel.querySelector('.label-suggestion');

      // if there are unread messages, scroll to the first unread message
      if (this.unreadMessageCount > 0) {
        // capturing only the unread messages
        relevantMessages =
          this.conversationPanel.querySelectorAll('.message--unread');
      } else if (labelSuggestions) {
        // when scrolling to the bottom, the label suggestions is below the last message
        // so we scroll there if there are no unread messages
        // Unread messages always take the highest priority
        relevantMessages = [labelSuggestions];
      } else {
        // if there are no unread messages or label suggestion, scroll to the last message
        // capturing last message from the messages list
        relevantMessages = Array.from(
          this.conversationPanel.querySelectorAll('.message--read')
        ).slice(-1);
      }

      let value = calculateScrollTop(
        this.conversationPanel.scrollHeight,
        this.$el.scrollHeight,
        relevantMessages
      );
      this.conversationPanel.scrollTop = value;
    },
    onToggleContactPanel() {
      this.$emit('contact-panel-toggle');
    },
    setScrollParams() {
      this.heightBeforeLoad = this.conversationPanel.scrollHeight;
      this.scrollTopBeforeLoad = this.conversationPanel.scrollTop;
    },

    async fetchPreviousMessages(scrollTop = 0) {
      this.setScrollParams();
      const shouldLoadMoreMessages =
        this.currentChat.dataFetched === true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < 100 &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        try {
          await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before:
              (this.currentChat.messages.length &&
                this.currentChat.messages[0].id) ||
              undefined, // d99d
          });
          const heightDifference =
            this.conversationPanel.scrollHeight - this.heightBeforeLoad;
          this.conversationPanel.scrollTop =
            this.scrollTopBeforeLoad + heightDifference;
          this.setScrollParams();
        } catch (error) {
          // Ignore Error
        } finally {
          this.isLoadingPrevious = false;
        }
      }
    },

    handleScroll(e) {
      if (this.isProgrammaticScroll) {
        // Reset the flag
        this.isProgrammaticScroll = false;
        this.hasUserScrolled = false;
      } else {
        this.hasUserScrolled = true;
      }
      bus.$emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
      this.fetchPreviousMessages(e.target.scrollTop);
    },

    async makeMessagesRead() {
      const needToUpdateNewCounter = this.getUnreadCount > 0;
      if (needToUpdateNewCounter) {
        await this.$store.dispatch('markMessagesRead', {
          id: this.currentChat.id,
        });
      }
    },

    async fetchSingleConversation() {
      await this.$store.dispatch('getConversation', this.currentChat.id);
    },

    getInReplyToMessage(parentMessage) {
      if (!parentMessage) return {};
      const inReplyToMessageId = parentMessage.content_attributes?.in_reply_to;
      if (!inReplyToMessageId) return {};

      return this.currentChat?.messages.find(message => {
        if (message.id === inReplyToMessageId) {
          return true;
        }
        return false;
      });
    },
    // d99d
    onCancelOnline99Fb2PrivateReply() {
      this.online99Fb2PrivateReplyMessage = undefined;
    },
    // .
  },
};
</script>

<style scoped>
@tailwind components;

@layer components {
  .rounded-bl-calc {
    border-bottom-left-radius: calc(1.5rem + 1px);
  }

  .rounded-tl-calc {
    border-top-left-radius: calc(1.5rem + 1px);
  }
}
</style>

<style scoped lang="scss">
:dir(rtl) {
  .ProseMirror-woot-style ul,
  .ProseMirror-woot-style ol {
    padding-left: 0;
    padding-right: 15px;
    margin-right: 0;
  }
}

.modal-mask {
  &::v-deep {
    .ProseMirror-woot-style {
      @apply max-h-[25rem];
    }

    .reply-box {
      @apply border border-solid border-slate-75 dark:border-slate-600 max-w-[75rem] w-[70%];
    }

    .reply-box .reply-box__top {
      @apply relative min-h-[27.5rem];
    }

    .reply-box__top .input {
      @apply min-h-[27.5rem];
    }

    .emoji-dialog {
      @apply absolute left-auto bottom-1;
    }
  }
}
</style>
